import { createIndexedDevices, getDevices, getInputDevicePermissionState, isIndexedDevices, isRequestedResolution, mergeConstraints, } from '@pexip/media-control';
import { assert, createAsyncQueue, isEmpty } from '@pexip/utils';
import { updateFeatureProps as getMixingFeatures } from './audioMixingProcessor';
import { updateFeatureProps as getAudioFeatures } from './audioProcessor';
import { DEVICE_CHANGE_DEBOUNCE_TIMEOUT_MS, GET_USER_MEDIA_TIMEOUT_MS, } from './constants';
import { createModuleLogger, logger } from './logger';
import { internalSignals } from './signals';
import { getPermissionStatus, isRequestPermissionNeeded } from './status';
import { isMedia } from './typeGuard';
import { areUserMediaStatusesEqual, createUserMediaStatus, EMPTY_USER_MEDIA_STATUS, formatUserMediaStatus, InputStatusMask, MediaInputStatusFlag, replaceInputStatus, updateInputStatus, } from './types';
import { createGetUserMediaProcess, deriveErrorStatus, deriveNoDeviceStatus, mergeInputErrorStatus, mergeMediaConstraints, mergePermissionStatus, requestUserMediaWithRetry, } from './userMedia';
import { AUDIO_SETTINGS_KEYS, buildMedia, createMediaProcessor, diffSettings, getDevicesChanges, getSettingsFromKeys, hasPtzFeature, isApplyingRenderEffect, MIXING_SETTINGS_KEYS, mergeSettings, PAN_TILT_ZOOM_SETTINGS_KEYS, refineMediaConstraints, VIDEO_SETTINGS_KEYS, } from './utils';
import { updateFeatureProps as getVideoFeatures, mapFeatureToSettings, mapSettingsToFeatures, } from './videoProcessor';
const requestedDeviceNotFoundStatus = MediaInputStatusFlag.Requested |
    MediaInputStatusFlag.DeviceAvailable |
    MediaInputStatusFlag.RequestedDeviceNotFound;
export const updateStatusForReleasedInputs = (status, permission, released) => {
    const permissionStatus = getPermissionStatus(permission);
    const deviceFacts = MediaInputStatusFlag.DeviceAvailable | MediaInputStatusFlag.NoDevice;
    const releasedStatus = (kind) => {
        const currentPermission = status[kind] & InputStatusMask.Permission;
        const observedPermission = permissionStatus[kind];
        const permission = currentPermission === MediaInputStatusFlag.PermissionDenied &&
            observedPermission === MediaInputStatusFlag.PermissionPrompt
            ? currentPermission
            : observedPermission;
        return permission | (status[kind] & deviceFacts);
    };
    return createUserMediaStatus({
        audio: released.audio ? releasedStatus('audio') : status.audio,
        video: released.video ? releasedStatus('video') : status.video,
    });
};
/**
 * Converts the permission fact recorded for an input into a browser permission
 * state.
 *
 * @example
 * toPermissionState(MediaInputStatusFlag.PermissionGranted);
 */
const toPermissionState = (status) => {
    if (status & MediaInputStatusFlag.PermissionGranted) {
        return 'granted';
    }
    if (status & MediaInputStatusFlag.PermissionDenied) {
        return 'denied';
    }
    return 'prompt';
};
/**
 * Marks requested inputs as timed out and reconciles track-dependent facts
 * with the media tracks that still exist.
 *
 * @example
 * updateStatusForTimeout(status, {audio: true, video: false}, devices, tracks);
 */
export const updateStatusForTimeout = (status, updating, devices, tracks) => {
    const timedOutStatus = (kind) => {
        const deviceKind = kind === 'audio' ? 'audioinput' : 'videoinput';
        const permission = devices.anyAuthorizedDevice(deviceKind)
            ? MediaInputStatusFlag.PermissionGranted
            : status[kind] & InputStatusMask.Permission;
        const deviceAvailability = devices.size(deviceKind) > 0
            ? MediaInputStatusFlag.DeviceAvailable
            : MediaInputStatusFlag.None;
        const trackAndMatchMask = MediaInputStatusFlag.TrackPresent | InputStatusMask.Match;
        const hasTrack = tracks?.some(track => track.kind === deviceKind);
        const trackAndMatch = hasTrack === undefined
            ? status[kind] & trackAndMatchMask
            : hasTrack
                ? MediaInputStatusFlag.TrackPresent |
                    (status[kind] & InputStatusMask.Match)
                : MediaInputStatusFlag.None;
        return (((status[kind] | MediaInputStatusFlag.Requested) &
            ~(InputStatusMask.Permission |
                MediaInputStatusFlag.DeviceAvailable |
                trackAndMatchMask |
                InputStatusMask.Error)) |
            permission |
            deviceAvailability |
            trackAndMatch |
            MediaInputStatusFlag.TimedOut);
    };
    return createUserMediaStatus({
        audio: updating.audio ? timedOutStatus('audio') : status.audio,
        video: updating.video ? timedOutStatus('video') : status.video,
    });
};
export const applyConstraintsWithStatus = async (media, constraints, isCurrent = () => true) => {
    const requests = [
        constraints.audio
            ? {
                kind: 'audio',
                constraints: { audio: constraints.audio },
            }
            : undefined,
        constraints.video
            ? {
                kind: 'video',
                constraints: { video: constraints.video },
            }
            : undefined,
    ].filter(request => request !== undefined);
    const results = await Promise.allSettled(requests.map(request => media.applyConstraints(request.constraints)));
    let firstError;
    for (const [index, result] of results.entries()) {
        if (!isCurrent()) {
            break;
        }
        const request = requests[index];
        assert(request, 'Expected constraints for applyConstraints result');
        if (result.status === 'fulfilled') {
            media.status = replaceInputStatus(media.status, request.kind, InputStatusMask.Error, MediaInputStatusFlag.None);
            continue;
        }
        firstError ?? (firstError = result.reason);
        const error = result.reason instanceof Error
            ? result.reason
            : new Error(String(result.reason));
        media.status = mergeInputErrorStatus(media.status, deriveErrorStatus(error, request.constraints), request.kind);
    }
    if (firstError !== undefined) {
        throw firstError;
    }
};
export const createMediaUpdater = ({ getUserMedia, getCurrentDevices, onMediaTracksChanged, getInputDevicePermission = getInputDevicePermissionState, getDefaultConstraints, isTrackInvalidated = () => false, signals, }) => {
    return async (constraints, currentMedia, isCurrent = () => true, signal, force = false) => {
        const currentDevices = await getCurrentDevices(isCurrent);
        if (!isCurrent()) {
            return;
        }
        const permission = await getInputDevicePermission();
        if (!isCurrent()) {
            return;
        }
        const releaseAudio = constraints.audio === false || permission.audio === 'denied';
        const releaseVideo = constraints.video === false || permission.video === 'denied';
        if (releaseAudio || releaseVideo) {
            // Release related
            for (const track of currentMedia?.getTracks() ?? []) {
                if ((track.kind === 'audioinput' && releaseAudio) ||
                    (track.kind === 'videoinput' && releaseVideo)) {
                    await track.release(isCurrent);
                    if (!isCurrent()) {
                        return;
                    }
                    currentMedia?.removeTrack(track);
                }
            }
            if (!isCurrent()) {
                return;
            }
            if (currentMedia) {
                currentMedia.status = updateStatusForReleasedInputs(currentMedia.status, permission, { audio: releaseAudio, video: releaseVideo });
                currentMedia.setOriginalConstraints(mergeMediaConstraints(currentMedia.getOriginalConstraints(), constraints));
            }
            // When both are true, there is nothing to request
            if (releaseAudio && releaseVideo) {
                const media = currentMedia ??
                    buildMedia({
                        constraints,
                        permission,
                        devices: currentDevices,
                        status: getPermissionStatus(permission),
                        stream: undefined,
                        signals,
                        tracks: [],
                    });
                return onMediaTracksChanged(media, []);
            }
        }
        const requestableConstraints = {
            audio: permission.audio === 'denied' ? false : constraints.audio,
            video: permission.video === 'denied' ? false : constraints.video,
        };
        const hasRequestedInput = Boolean(requestableConstraints.audio || requestableConstraints.video);
        const hasAvailableRequestedInput = (Boolean(requestableConstraints.audio) &&
            currentDevices.size('audioinput') > 0) ||
            (Boolean(requestableConstraints.video) &&
                currentDevices.size('videoinput') > 0);
        if (!currentMedia && hasRequestedInput && !hasAvailableRequestedInput) {
            const media = buildMedia({
                constraints,
                permission,
                devices: currentDevices,
                status: mergePermissionStatus(deriveNoDeviceStatus(requestableConstraints), permission),
                stream: undefined,
                signals,
                tracks: [],
            });
            return onMediaTracksChanged(media, []);
        }
        const { audio: prevAudioSettings, video: prevVideoSettings } = currentMedia?.getSettings() ?? {};
        const videoFeatures = getVideoFeatures(constraints.video, {});
        const audioFeatures = getAudioFeatures(constraints.audio, {});
        const mixingFeatures = getMixingFeatures(constraints.audio, {});
        const hasRequestedResolution = isApplyingRenderEffect(videoFeatures.videoSegmentation ?? 'none') || // Skip when there is a render effect to modify the video
            currentDevices.size('videoinput') === 0 || // Skip when no authorized video found
            isRequestedResolution(constraints.video, currentMedia?.getVideoTracks().at(0)?.getSettings());
        const defaultAudioSettings = getSettingsFromKeys(AUDIO_SETTINGS_KEYS, getDefaultConstraints?.().audio);
        const audioFeaturesChanged = defaultAudioSettings === false
            ? undefined
            : diffSettings(AUDIO_SETTINGS_KEYS)(prevAudioSettings ?? defaultAudioSettings, audioFeatures);
        const defaultVideoSettings = getSettingsFromKeys(VIDEO_SETTINGS_KEYS, getDefaultConstraints?.().video);
        const videoFeaturesChanged = defaultVideoSettings === false
            ? undefined
            : diffSettings(VIDEO_SETTINGS_KEYS)(prevVideoSettings ?? defaultVideoSettings, mapFeatureToSettings(videoFeatures));
        const defaultMixingSettings = getSettingsFromKeys(MIXING_SETTINGS_KEYS, getDefaultConstraints?.().audio);
        const mixingFeaturesChanged = defaultMixingSettings === false
            ? undefined
            : diffSettings(MIXING_SETTINGS_KEYS)(prevAudioSettings ?? defaultMixingSettings, mixingFeatures);
        const defaultPTZSettings = getSettingsFromKeys(PAN_TILT_ZOOM_SETTINGS_KEYS, getDefaultConstraints?.().video);
        const ptzFeaturesChanges = defaultPTZSettings === false
            ? undefined
            : hasPtzFeature() &&
                diffSettings(PAN_TILT_ZOOM_SETTINGS_KEYS)(prevVideoSettings ?? defaultPTZSettings, mapFeatureToSettings(videoFeatures));
        const audioRequest = refineMediaConstraints({
            kind: 'audioinput',
            request: constraints.audio,
            currentDevices,
            permission: permission.audio,
            currentMediaTracks: currentMedia?.getAudioTracks() ?? [],
            force: force ||
                (currentMedia?.getAudioTracks().some(isTrackInvalidated) ??
                    false),
        });
        const videoRequest = refineMediaConstraints({
            kind: 'videoinput',
            request: constraints.video,
            currentDevices,
            permission: permission.video,
            currentMediaTracks: currentMedia?.getVideoTracks() ?? [],
            force: force ||
                !hasRequestedResolution ||
                !isEmpty(ptzFeaturesChanges) ||
                (currentMedia?.getVideoTracks().some(isTrackInvalidated) ??
                    false),
        });
        if (audioRequest || videoRequest) {
            const media = await getUserMedia({
                constraints: { audio: audioRequest, video: videoRequest },
                originalConstraints: constraints,
                permission,
                currentMedia,
                isCurrent,
                signal,
            });
            if (!isCurrent()) {
                logger.debug('Discard stale media');
                if (media !== currentMedia) {
                    await media.release(isCurrent);
                }
                return;
            }
            onMediaTracksChanged(media, [
                ...(audioRequest ? media.getAudioTracks() : []),
                ...(videoRequest ? media.getVideoTracks() : []),
            ]);
        }
        else if (currentMedia?.status) {
            logger.debug({ status: formatUserMediaStatus(currentMedia.status) }, 'Update existing media status');
            currentMedia.setOriginalConstraints(constraints);
        }
        const audioDiff = constraints.audio &&
            audioRequest === undefined &&
            currentMedia?.getAudioTracks().at(0)
            ? mergeSettings(audioFeaturesChanged, mixingFeaturesChanged)
            : undefined;
        const videoDiff = constraints.video &&
            videoRequest === undefined &&
            currentMedia?.getVideoTracks().at(0)
            ? videoFeaturesChanged
            : undefined;
        if (audioDiff || videoDiff) {
            if (currentMedia) {
                await applyConstraintsWithStatus(currentMedia, {
                    audio: audioDiff && mapSettingsToFeatures(audioDiff),
                    video: videoDiff && mapSettingsToFeatures(videoDiff),
                }, isCurrent);
            }
        }
    };
};
/**
 * Proxy handler for Media Props
 */
const createMediaPropsHandler = (signals) => ({
    get: (target, p) => {
        switch (p) {
            default:
                return target[p];
        }
    },
    set: (target, p, value) => {
        if (target[p] === value) {
            return true;
        }
        if (p === 'devices') {
            if (!isIndexedDevices(value)) {
                return false;
            }
            const nextDevices = value;
            const changes = getDevicesChanges(target[p].get(), nextDevices.get());
            if (isEmpty(changes.found) && isEmpty(changes.lost)) {
                return true;
            }
        }
        logger.debug({
            oldValue: target[p],
            newValue: value,
            meta: {
                module: 'Media',
                props: target,
            },
        }, `Update Props[${p}]`);
        switch (p) {
            case 'devices': {
                if (!isIndexedDevices(value)) {
                    return false;
                }
                target[p] = value;
                signals?.onDevicesChanged?.emit(target[p]);
                internalSignals.onDevicesChanged.emit(target[p]);
                return true;
            }
            case 'media': {
                if (!isMedia(value)) {
                    return false;
                }
                const currentStatus = target[p]?.status;
                target[p] = value;
                signals?.onMediaChanged?.emit(value);
                if (!currentStatus ||
                    !areUserMediaStatusesEqual(currentStatus, value.status)) {
                    signals?.onStatusChanged?.emit(value.status);
                }
                return true;
            }
            case 'updatingAudio': {
                if (target[p] === value) {
                    return true;
                }
                const result = Reflect.set(target, p, value);
                signals?.onUpdatingAudio?.emit(value);
                return result;
            }
            case 'updatingVideo': {
                if (target[p] === value) {
                    return true;
                }
                const result = Reflect.set(target, p, value);
                signals?.onUpdatingVideo?.emit(value);
                return result;
            }
            default: {
                return Reflect.set(target, p, value);
            }
        }
    },
});
/**
 * Create an object to interact with the media scream, which is usually used for
 * our main stream.
 *
 * @param options - @see MediaOptions
 */
export const createMedia = ({ getMuteState, signals, audioProcessors, videoProcessors, stopVideoTrackAsMute = () => true, getDefaultConstraints = () => ({}), }) => {
    const _props = {
        devices: createIndexedDevices([]),
        updatingAudio: false,
        updatingVideo: false,
    };
    const props = new Proxy(_props, createMediaPropsHandler(signals));
    let updateGeneration = 0;
    let queuedUpdate;
    const invalidatedTracks = new WeakSet();
    const queue = createAsyncQueue({
        timeoutInMS: GET_USER_MEDIA_TIMEOUT_MS,
        handleTimeout: async () => {
            const timedOutUpdate = queuedUpdate;
            if (!timedOutUpdate) {
                return;
            }
            timedOutUpdate.abortController.abort();
            queuedUpdate = undefined;
            if (timedOutUpdate.generation !== updateGeneration) {
                return;
            }
            updateGeneration += 1;
            for (const track of props.media?.getTracks() ?? []) {
                if ((timedOutUpdate.audio && track.kind === 'audioinput') ||
                    (timedOutUpdate.video && track.kind === 'videoinput')) {
                    invalidatedTracks.add(track);
                }
            }
            if (timedOutUpdate.audio) {
                props.updatingAudio = false;
            }
            if (timedOutUpdate.video) {
                props.updatingVideo = false;
            }
            const status = updateStatusForTimeout(props.media?.status ?? EMPTY_USER_MEDIA_STATUS, {
                audio: timedOutUpdate.audio,
                video: timedOutUpdate.video,
            }, props.devices, props.media?.getTracks());
            if (props.media) {
                props.media.status = status;
            }
            else {
                props.media = buildMedia({
                    constraints: timedOutUpdate.constraints,
                    permission: {
                        audio: toPermissionState(status.audio),
                        video: toPermissionState(status.video),
                    },
                    devices: props.devices,
                    status,
                    stream: undefined,
                    signals,
                    tracks: [],
                });
            }
        },
    });
    const logger = createModuleLogger({
        module: 'Media',
        props: _props,
        get mediaTracks() {
            return _props.media?.stream?.getTracks();
        },
    });
    const getCurrentDevices = async () => {
        const devices = createIndexedDevices(await getDevices());
        props.devices = devices;
        return devices;
    };
    const processMedia = createMediaProcessor({
        audioProcessors,
        videoProcessors,
        onProcessingError(error, track) {
            logger.error({ error, track }, 'Failed to process media track');
            if (!props.media ||
                invalidatedTracks.has(track) ||
                !props.media.isCurrentTrack(track)) {
                return;
            }
            switch (track.kind) {
                case 'audioinput':
                    // FIXME: It should not be device-not-found error, will be fixed with https://gitlab.com/pexip/zoo/-/issues/3793
                    props.media.status = updateInputStatus(props.media.status, 'audio', requestedDeviceNotFoundStatus);
                    break;
                case 'videoinput':
                    // FIXME: It should not be device-not-found error, will be fixed with https://gitlab.com/pexip/zoo/-/issues/3793
                    props.media.status = updateInputStatus(props.media.status, 'video', requestedDeviceNotFoundStatus);
                    break;
            }
        },
    });
    const getUserMediaProcess = createGetUserMediaProcess({
        getCurrentDevices,
        getUserMedia: requestUserMediaWithRetry(getCurrentDevices),
        signals,
        stopVideoTrackAsMute,
        updateMedia: constraints => enqueueMediaUpdate(constraints),
    });
    const processAndUpdateMedia = async (media, tracks) => {
        const processedTracks = await processMedia(tracks);
        for (const [idx, track] of processedTracks.entries()) {
            const originTrack = tracks.at(idx);
            assert(originTrack, 'Processed track should always has the original track in the same order');
            if (originTrack.stopped ||
                media !== props.media ||
                invalidatedTracks.has(originTrack) ||
                !media.isCurrentTrack(originTrack)) {
                if (originTrack.id !== track.id) {
                    await track.release(() => false);
                }
                continue;
            }
            // Only replace track when they are not the same
            if (originTrack.id !== track.id) {
                if (originTrack.muted !== undefined) {
                    track.mute(originTrack.muted);
                }
                media.removeTrack(originTrack);
                media.addTrack(track);
            }
        }
    };
    const updateMediaProcess = createMediaUpdater({
        signals,
        getUserMedia: getUserMediaProcess,
        getCurrentDevices,
        getDefaultConstraints,
        isTrackInvalidated: track => invalidatedTracks.has(track),
        onMediaTracksChanged: (media, tracks) => {
            const inputMuted = getMuteState();
            for (const track of tracks) {
                // Sync mute state to input tracks
                track.mute(track.kind === 'audioinput'
                    ? inputMuted.audio
                    : inputMuted.video);
            }
            logger.debug({ media, tracks }, 'Update media tracks');
            props.media = media;
            processAndUpdateMedia(media, tracks).catch((error) => {
                if (error instanceof Error) {
                    logger.error(error, 'Failed to process media');
                }
            });
        },
    });
    /**
     * A function to update the media only when necessary, aka the current setup
     * for the stream is different from the new constraints
     *
     * @param constraints - @see MediaDeviceRequest
     */
    const beginUpdate = (constraints) => {
        const updateAudio = constraints.audio !== undefined;
        const updateVideo = constraints.video !== undefined;
        const update = {
            generation: ++updateGeneration,
            audio: updateAudio,
            video: updateVideo,
            constraints,
            abortController: new AbortController(),
        };
        queuedUpdate = update;
        if (updateAudio) {
            props.updatingAudio = true;
        }
        if (updateVideo) {
            props.updatingVideo = true;
        }
        return update;
    };
    const finishUpdate = (update) => {
        const isCurrent = update.generation === updateGeneration;
        if (isCurrent && update.audio) {
            props.updatingAudio = false;
        }
        if (isCurrent && update.video) {
            props.updatingVideo = false;
        }
        if (queuedUpdate?.generation === update.generation) {
            queuedUpdate = undefined;
        }
    };
    const updateMedia = async (constraints, context) => {
        const update = context ?? beginUpdate(constraints);
        const ownsContext = context === undefined;
        const isCurrent = () => update.generation === updateGeneration;
        try {
            await updateMediaProcess(constraints, props.media, isCurrent, update.abortController.signal);
        }
        finally {
            if (ownsContext) {
                finishUpdate(update);
            }
        }
    };
    const enqueueMediaUpdate = async (constraints) => {
        queue.enqueue(async () => await updateMedia(constraints));
        await queue.execute();
    };
    const mergeMediaConstraints = (constraints) => {
        const { audio, video } = getDefaultConstraints();
        return {
            audio: audio === false
                ? false
                : mergeConstraints(audio)(constraints.audio),
            video: video === false
                ? false
                : mergeConstraints(video)(constraints.video),
        };
    };
    const getUserMediaAsync = async (constraints) => {
        queue.enqueue(async () => await updateMedia(mergeMediaConstraints(constraints)), false);
        await queue.execute();
    };
    const getUserMedia = constraints => {
        queue.enqueue(async () => await updateMedia(mergeMediaConstraints(constraints)));
    };
    const tryAndGetUserMedia = constraints => {
        queue.enqueue(async () => {
            const mergedConstraints = mergeMediaConstraints(constraints);
            const update = beginUpdate(mergedConstraints);
            const isCurrent = () => update.generation === updateGeneration;
            try {
                const permission = await getInputDevicePermissionState();
                if (!isCurrent()) {
                    return;
                }
                const status = getPermissionStatus(permission);
                logger.info({ permission, status: formatUserMediaStatus(status) }, 'tryAndGetUserMedia');
                if (!isRequestPermissionNeeded(status)) {
                    await updateMedia(mergedConstraints, update);
                }
                else {
                    const currentDevices = await getCurrentDevices(isCurrent);
                    if (!isCurrent()) {
                        return;
                    }
                    props.media = buildMedia({
                        constraints: mergedConstraints,
                        permission,
                        devices: currentDevices,
                        status,
                        stream: undefined,
                        signals,
                        tracks: [],
                    });
                }
            }
            finally {
                finishUpdate(update);
            }
        });
    };
    let deviceChangeTimeoutId = 0;
    const clearDeviceChangeTimeout = () => {
        if (deviceChangeTimeoutId) {
            clearTimeout(deviceChangeTimeoutId);
            deviceChangeTimeoutId = 0;
        }
    };
    const handleDeviceChange = () => {
        logger.debug('devicechange event triggered');
        clearDeviceChangeTimeout();
        deviceChangeTimeoutId = setTimeout(getCurrentDevices, DEVICE_CHANGE_DEBOUNCE_TIMEOUT_MS);
    };
    // Subscribe Media Events for devices
    if ('mediaDevices' in navigator) {
        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }
    return {
        get updatingAudio() {
            return props.updatingAudio;
        },
        get updatingVideo() {
            return props.updatingVideo;
        },
        get media() {
            return props.media;
        },
        get devices() {
            return props.devices;
        },
        getUserMedia,
        getUserMediaAsync,
        tryAndGetUserMedia,
    };
};
//# sourceMappingURL=media.js.map